/**
 * pushApi.js + usePushSubscription hook + helpers.
 *
 * Exposes:
 *  - getPublicKey() / getPreferences() / subscribe() / unsubscribe() / sendTest()
 *  - urlBase64ToUint8Array(b64), converts the VAPID public key to the format
 *    PushManager.subscribe expects.
 *  - canUsePush(), feature detection
 */
import axios from "axios";
import { BACKEND_URL } from "./backendBase";

const client = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

export const pushApi = {
  publicKey: () => client.get("/api/push/public-key").then((r) => r.data),
  preferences: () => client.get("/api/push/preferences").then((r) => r.data),
  subscribe: (subscription) =>
    client.post("/api/push/subscribe", subscription).then((r) => r.data),
  unsubscribe: (endpoint) =>
    client.post("/api/push/unsubscribe", { endpoint }).then((r) => r.data),
  test: () => client.post("/api/push/test").then((r) => r.data),
};

export const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const norm = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(norm);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const canUsePush = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/** Returns the active PushSubscription (or null). */
export const getCurrentPushSubscription = async () => {
  if (!canUsePush()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

/**
 * Subscribe to push:
 *  1) request browser permission
 *  2) PushManager.subscribe with VAPID public key
 *  3) POST to backend
 */
export const enablePush = async () => {
  if (!canUsePush()) throw new Error("Notificações não suportadas neste navegador.");
  if (Notification.permission === "denied") {
    throw new Error(
      "Permissão bloqueada. Abra as configurações do navegador e permita notificações.",
    );
  }
  // Get VAPID public key first (so we know push is configured server-side)
  const { public_key: publicKey } = await pushApi.publicKey();
  if (!publicKey) throw new Error("Servidor sem chave VAPID configurada.");

  // Ask browser permission
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permissão negada.");

  // Subscribe via SW
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  // Persist on backend (auth required)
  await pushApi.subscribe(sub.toJSON());
  return sub;
};

export const disablePush = async () => {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  const endpoint = sub?.endpoint;
  if (sub) await sub.unsubscribe();
  // Also wipe on backend
  try { await pushApi.unsubscribe(endpoint); } catch { /* ignore */ }
};
